import {
    Component,
    DestroyRef,
    effect,
    EnvironmentInjector,
    HostListener,
    inject,
    Injector,
    OnInit
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { IonicModule, NavController } from '@ionic/angular';
import { TranslocoService } from '@ngneat/transloco';
import { concatLatestFrom } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { tuiIsPresent } from '@taiga-ui/cdk';
import { TuiAlertService, TuiDialogService } from '@taiga-ui/core';
import { PolymorpheusComponent } from '@tinkoff/ng-polymorpheus';
import { ENVIRONMENT } from '@us/app-config';
import { CartActions, selectCart, selectCartTable, selectOrderId, selectOrderType } from '@us/web/cart/data-access';
import { CategoriesStore } from '@us/web/categories/data-access';
import { AppInit } from '@us/web/shared/app';
import { LocalStorageService, OrderState, OrderType } from '@us/web/shared/data-access';
import { isFnBApp, RouteUtil } from '@us/web/shared/utils';
import {
    selectColors,
    selectMergeOrderQr,
    selectOrderTypes,
    selectShop,
    selectShopId,
    selectTurnOnEnterNumberGuest,
    selectTurnOnTheStoreChainLinkFeature
} from '@us/web/shop/data-access';
import { selectSelectedShopId } from '@us/web/shops/data-access';
import {
    combineLatest,
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    Observable,
    pairwise,
    startWith,
    switchMap,
    take,
    tap
} from 'rxjs';
import { TuiSheetOptions, TuiSheetService } from '@taiga-ui/addon-mobile';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { CartTableNumberGuestComponent, JoinOrderComponent } from '@us/web/cart/feature';
import { OrdersActions } from '@us/web/orders/data-access';
import { PusherService } from '@us/web/websockets/data-access';
import { OpenBrowserComponent } from '@us/web/shared/ui';

@Component({
    selector: 'us-main',
    templateUrl: './main.page.html',
    styleUrls: ['./main.page.scss'],
    standalone: true,
    imports: [IonicModule, RouterModule]
})
export class MainPage implements OnInit {
    private readonly store = inject(Store);
    private readonly route = inject(ActivatedRoute);
    // private readonly messagingService = inject(MessagingService);
    // private readonly authService = inject(AuthService);
    private readonly localStorage = inject(LocalStorageService);
    public readonly environmentInjector = inject(EnvironmentInjector);
    private readonly env = inject(ENVIRONMENT);
    private readonly categoriesStore = inject(CategoriesStore);
    // private readonly allItems$ = this.categoriesStore.allItems$;
    private readonly dialogService = inject(TuiDialogService);
    private readonly injector = inject(Injector);
    private readonly destroyRef = inject(DestroyRef);
    private readonly sheetService = inject(TuiSheetService);
    private readonly translocoService = inject(TranslocoService);
    private readonly pusherService = inject(PusherService);
    private readonly alertService = inject(TuiAlertService);
    private readonly navCtrl = inject(NavController);
    private readonly router = inject(Router);

    private readonly navigationEnd$ = this.router.events.pipe(
        filter(event => event instanceof NavigationEnd)
    ) as Observable<NavigationEnd>;

    private readonly shopIdParams$ = (isFnBApp(this.env)
        ? this.store.select(selectShopId)
        : this.store
              .select(selectTurnOnTheStoreChainLinkFeature)
              .pipe(
                  switchMap(chain =>
                      chain ? this.store.select(selectSelectedShopId) : this.store.select(selectShopId)
                  )
              )
    ).pipe(filter(tuiIsPresent), distinctUntilChanged());

    private readonly cartId$ = this.route.queryParams.pipe(
        map(params => params['cartId'] as string),
        filter(cartId => !!cartId),
        distinctUntilChanged()
    );

    private readonly tableId$ = this.route.queryParams.pipe(
        map(params => Number(params['tableId'])),
        filter(tableId => !!tableId),
        distinctUntilChanged()
    );

    private readonly colors = this.store.selectSignal(selectColors);

    @HostListener('window:resize', ['$event'])
    onResize(): void {
        const doc = document.documentElement;
        doc.style.setProperty('--app-height', `${window.innerHeight}px`);
    }

    openBrowser = false;

    constructor() {
        // effect(() => {
        //     const user = this.user();
        //     const sessionUser = this.sessionUser();
        //     const id = user?.id || sessionUser?.id || null;
        //     if (id) {
        //         this.messagingService.subscribeNotiList(id);
        //     }
        // });
        effect(() => {
            const colors = this.colors();
            if (!colors) {
                return;
            }
            const rootElement = document.querySelector(':root') as HTMLElement;
            if (colors.primary) {
                rootElement.style.setProperty('--tui-primary', colors.primary);
                rootElement.style.setProperty('--tui-primary-active', colors.primary);
                rootElement.style.setProperty('--ion-color-primary', colors.primary);
            }

            if (colors.secondary) {
                rootElement.style.setProperty('--tui-secondary', colors.secondary);
                rootElement.style.setProperty('--ion-color-secondary', colors.secondary);
            }

            if (colors.base01) {
                rootElement.style.setProperty('--tui-base-01', colors.base01);
            }

            // if (colors.base02) {
            //     rootElement.style.setProperty('--tui-base-02', colors.base02);
            // }

            if (colors.header) {
                rootElement.style.setProperty('--bg-header', colors.header);
            }
        });
    }

    ngOnInit() {
        // check open web in zalo
        if (/Zalo/i.test(navigator.userAgent)) {
            this.sheetService
                .open<void>(new PolymorpheusComponent(OpenBrowserComponent, this.injector), {
                    dismissible: false,
                    overlay: true,
                    closeable: false,
                    data: {
                        isAndroid: !/iPhone|iPad|iPod/i.test(navigator.userAgent),
                        url: window.location.origin + this.router.url
                    }
                } as unknown as Partial<TuiSheetOptions>)
                .subscribe();
            // https://stackoverflow.com/questions/39602334/how-to-open-mobile-chrome-browser-with-javascript-or-html/70066226#70066226
            return;
        }
        this.store.dispatch(AppInit({}));
        this.store
            .select(selectShop)
            .pipe(
                filter(shop => !!shop),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(shop => {
                const shopId: string | null = this.localStorage.getItem('shop_id');
                if (shopId !== null && shopId !== shop.id) {
                    this.store.dispatch(CartActions.clearCart({}));
                }
            });

        this.store
            .select(selectOrderId)
            .pipe(
                filter(orderId => !!orderId),
                switchMap(() => this.tableId$),
                startWith(undefined),
                pairwise(),
                concatLatestFrom(() => this.store.select(selectCart)),
                tap(async ([[prevTableId, tableId], cart]) => {
                    const is_merge_order_qr = localStorage.getItem('is_merge_order_qr');
                    if (is_merge_order_qr) {
                        localStorage.removeItem('is_merge_order_qr');
                        return;
                    }
                    const flg_merge_order = this.store.selectSignal(selectMergeOrderQr)();
                    if (cart.current_state !== OrderState.Pending) {
                        if (flg_merge_order) {
                            if (tableId !== cart.order_table?.id) {
                                await this.showErrorSelectTable();
                                return;
                            }
                        } else {
                            await this.showErrorSelectTable();
                            return;
                        }
                    }

                    if (
                        tableId !== undefined &&
                        !isNaN(tableId) &&
                        tableId !== prevTableId &&
                        tableId !== cart.order_table?.id
                    ) {
                        const shopId = this.route.snapshot.queryParams['id'];
                        this.store.dispatch(CartActions.selectTable({ tableId, shopId }));
                    }
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();

        this.onResize();
        this.onChangeCart();

        combineLatest([this.cartId$, this.translocoService.selectTranslate('group_order').pipe(take(1))])
            .pipe(
                switchMap(([cartId, label]) =>
                    this.dialogService.open(new PolymorpheusComponent(JoinOrderComponent, this.injector), {
                        size: 's',
                        label,
                        data: { cartId }
                    })
                ),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();

        this.pusherService.orderUpdated$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(({ order, title, body }) => {
                this.store.dispatch(OrdersActions.loadOrderSuccess({ order, title, body }));
            });

        this.getCartDetail();

        this.navigationEnd$
            .pipe(
                filter(e => e.url === '/' || e.url === '/cart'),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: () => {
                    this.validateOrderType();
                }
            });
    }

    private getCartDetail() {
        this.store
            .select(selectOrderId)
            .pipe(
                take(1),
                tap(id => {
                    if (id) {
                        this.store.dispatch(CartActions.getCartDetail());
                    }
                })
            )
            .subscribe();
    }

    private async showErrorSelectTable() {
        this.translocoService
            .selectTranslate('can_not_change_table')
            .pipe(
                take(1),
                switchMap(message => this.alertService.open(message, { status: 'warning' }))
            )
            .subscribe();

        await this.navCtrl.navigateRoot([RouteUtil.getCartRouteUrl()]);
    }

    private validateOrderType() {
        const orderType = this.store.selectSignal(selectOrderType)();
        const orderTypes = this.store.selectSignal(selectOrderTypes)();

        if (!orderType && orderTypes.length > 0) {
            if (orderTypes.length === 1) {
                if (orderTypes[0] !== OrderType.OrderAtTheTable) {
                    this.store.dispatch(CartActions.updateCart({ order_type: orderTypes[0] }));
                }
            } else {
                // timer(2000)
                //     .pipe(
                //         switchMap(() =>
                //             this.sheetService.open<{
                //                 orderType: OrderType;
                //                 table?: Table;
                //                 isChanged: boolean;
                //             }>(new PolymorpheusComponent(OrderTypeSelectorComponent, this.injector), {
                //                 closeable: true,
                //                 overlay: true
                //             } as Partial<TuiSheetOptions>)
                //         ),
                //         tap(({ orderType, table, isChanged }) => {
                //             if (!isChanged) {
                //                 return;
                //             }
                //             const data: CartFillable = { order_type: orderType };
                //
                //             if (table) {
                //                 data.order_table = table;
                //             }
                //
                //             if (orderType !== OrderType.OrderAtTheTable || !!table) {
                //                 this.store.dispatch(CartActions.updateCart(data));
                //             }
                //         })
                //     )
                //     .subscribe();
            }
        }
    }

    private onChangeCart() {
        // TODO: promotion
        // this.store
        //     .select(selectPromotions)
        //     .pipe(takeUntilDestroyed(this.destroyRef), combineLatestWith(this.allItems$))
        //     .subscribe(([promotions, items]) => {
        //         if (items && items.length) this.store.dispatch(CartActions.updateCart({ productList: items }));
        //     });

        if (this.store.selectSignal(selectTurnOnEnterNumberGuest)()) {
            this.store
                .select(selectCartTable)
                .pipe(
                    takeUntilDestroyed(this.destroyRef),
                    filter(table => !!table && !!table.id && !table.amount_of_people),
                    debounceTime(1000),
                    switchMap(table =>
                        this.dialogService.open(
                            new PolymorpheusComponent(CartTableNumberGuestComponent, this.injector),
                            {
                                dismissible: false,
                                closeable: false,
                                data: {
                                    table
                                }
                            }
                        )
                    )
                )
                .subscribe();
        }
    }
}
